import { useState, useRef } from 'react';
import { storage } from '../../services/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import AdminLayout from '../../components/admin/AdminLayout';
import { useTenantCollection, useTenantCrud } from '../../hooks/useTenantData';
import { useClub } from '../../contexts/ClubContext';

function ManageGallery() {
  const { club, clubId } = useClub();
  const { data: albums, loading, setData: setAlbums } = useTenantCollection('galleries');
  const { add, update, remove } = useTenantCrud('galleries');
  
  const [showModal, setShowModal] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    isPublic: true,
    photos: []
  });
  
  const fileInputRef = useRef(null);

  // Sort albums by date
  const sortedAlbums = [...albums].sort((a, b) => {
    const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
    const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
    return dateB - dateA;
  });

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      isPublic: true,
      photos: []
    });
    setEditingAlbum(null);
    setError('');
    setSuccess('');
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (album) => {
    setEditingAlbum(album);
    setFormData({
      title: album.title || '',
      description: album.description || '',
      isPublic: album.isPublic !== false,
      photos: album.photos || []
    });
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    try {
      const uploadedPhotos = [];

      for (const file of files) {
        // Validate file
        if (!file.type.startsWith('image/')) {
          continue; // Skip non-images
        }
        if (file.size > 5 * 1024 * 1024) {
          setError(`${file.name} is too large (max 5MB)`);
          continue;
        }

        // Upload to Firebase Storage
        const timestamp = Date.now();
        const filename = `${clubId}/gallery/${timestamp}_${file.name}`;
        const storageRef = ref(storage, `gallery/${filename}`);
        
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        uploadedPhotos.push({
          url: downloadURL,
          storagePath: `gallery/${filename}`,
          filename: file.name,
          caption: '',
          uploadedAt: new Date().toISOString()
        });
      }

      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...uploadedPhotos]
      }));

      setSuccess(`Uploaded ${uploadedPhotos.length} photo(s)`);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (err) {
      console.error('Error uploading photos:', err);
      setError('Failed to upload some photos');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (index) => {
    const photo = formData.photos[index];
    
    // If it's an uploaded photo with storage path, delete from storage
    if (photo.storagePath) {
      try {
        const storageRef = ref(storage, photo.storagePath);
        await deleteObject(storageRef);
      } catch (err) {
        console.error('Error deleting photo from storage:', err);
      }
    }

    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const updateCaption = (index, caption) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.map((p, i) => i === index ? { ...p, caption } : p)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    if (!formData.title) {
      setError('Please enter an album title');
      setSaving(false);
      return;
    }

    try {
      const albumData = {
        title: formData.title,
        description: formData.description,
        isPublic: formData.isPublic,
        photos: formData.photos,
        coverPhoto: formData.photos[0]?.url || null,
        photoCount: formData.photos.length
      };

      if (editingAlbum) {
        await update(editingAlbum.id, albumData);
        setAlbums(albums.map(a => 
          a.id === editingAlbum.id ? { ...a, ...albumData } : a
        ));
        setSuccess('Album updated!');
      } else {
        const newAlbum = await add(albumData);
        setAlbums([...albums, newAlbum]);
        setSuccess('Album created!');
      }

      setTimeout(() => {
        setShowModal(false);
        resetForm();
      }, 1500);

    } catch (err) {
      console.error('Error saving album:', err);
      setError('Failed to save album');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (albumId, albumTitle) => {
    if (!window.confirm(`Delete album "${albumTitle}" and all its photos?`)) return;

    try {
      const album = albums.find(a => a.id === albumId);
      
      // Delete all photos from storage
      if (album?.photos) {
        for (const photo of album.photos) {
          if (photo.storagePath) {
            try {
              const storageRef = ref(storage, photo.storagePath);
              await deleteObject(storageRef);
            } catch (err) {
              console.error('Error deleting photo:', err);
            }
          }
        }
      }

      await remove(albumId);
      setAlbums(albums.filter(a => a.id !== albumId));
    } catch (err) {
      console.error('Error deleting album:', err);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-TT', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <AdminLayout title="Photo Gallery" subtitle={`Manage photo albums for ${club?.name || 'your club'}`}>
      <div className="admin-page-header">
        <div></div>
        <button className="btn-add" onClick={openAddModal}>
          + Create Album
        </button>
      </div>

      {/* Albums Grid */}
      <div className="admin-card">
        <div className="card-content">
          {loading ? (
            <p className="loading-text">Loading albums...</p>
          ) : sortedAlbums.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">📸</span>
              <h3>No Photo Albums</h3>
              <p>Create your first album to start sharing photos!</p>
              <button className="btn-add" onClick={openAddModal}>Create Album</button>
            </div>
          ) : (
            <div className="gallery-admin-grid">
              {sortedAlbums.map(album => (
                <div key={album.id} className="gallery-admin-card">
                  <div className="gallery-admin-cover">
                    {album.coverPhoto || (album.photos && album.photos[0]) ? (
                      <img 
                        src={album.coverPhoto || album.photos[0].url} 
                        alt={album.title} 
                      />
                    ) : (
                      <div className="gallery-placeholder">📷</div>
                    )}
                    <div className="gallery-photo-count">
                      {album.photos?.length || 0} photos
                    </div>
                    {!album.isPublic && (
                      <div className="gallery-private-badge">Private</div>
                    )}
                  </div>
                  <div className="gallery-admin-info">
                    <h3>{album.title}</h3>
                    <p className="gallery-date">{formatDate(album.createdAt)}</p>
                  </div>
                  <div className="gallery-admin-actions">
                    <button 
                      className="btn-edit"
                      onClick={() => openEditModal(album)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDelete(album.id, album.title)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Album Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-xlarge" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAlbum ? 'Edit Album' : 'Create Album'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="form-error">{error}</div>}
                {success && <div className="form-success">{success}</div>}

                <div className="form-group">
                  <label>Album Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., U14 Championship 2026"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    placeholder="Optional description for this album"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isPublic}
                      onChange={e => setFormData({...formData, isPublic: e.target.checked})}
                    />
                    Make album public (visible on gallery page)
                  </label>
                </div>

                {/* Photo Upload */}
                <div className="form-group">
                  <label>Photos</label>
                  <div className="photo-upload-area">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handlePhotoUpload}
                      accept="image/*"
                      multiple
                      disabled={uploading}
                    />
                    {uploading && <span className="uploading-text">Uploading...</span>}
                    <p className="upload-hint">Select multiple images (max 5MB each)</p>
                  </div>
                </div>

                {/* Photo Preview Grid */}
                {formData.photos.length > 0 && (
                  <div className="photo-preview-grid">
                    {formData.photos.map((photo, index) => (
                      <div key={index} className="photo-preview-item">
                        <img src={photo.url} alt={`Photo ${index + 1}`} />
                        <input
                          type="text"
                          placeholder="Caption (optional)"
                          value={photo.caption || ''}
                          onChange={(e) => updateCaption(index, e.target.value)}
                          className="photo-caption-input"
                        />
                        <button 
                          type="button"
                          className="photo-remove"
                          onClick={() => removePhoto(index)}
                        >
                          ×
                        </button>
                        {index === 0 && (
                          <span className="cover-badge">Cover</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving || uploading}>
                  {saving ? 'Saving...' : (editingAlbum ? 'Update Album' : 'Create Album')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ManageGallery;
